import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
    addFavoriteDealAPI, 
    checkFavoriteDealAPI, 
    createWholesaleFavoriteAPI, 
    deleteFavoriteAPI, 
    deleteWholesaleListAPI, 
    exportWholesaleListAPI,
    getWholesaleSettingsAPI,
    getWholesaleAllFavoriteAPI,
    getWholesaleAllFilesAPI, 
    postWholesaleAIResponseAPI,
    removeFavoriteDealAPI, 
    updateWholesaleSettingsAPI,
    wholesaleUploadAPI 
} from '../lib/api/api'

export let useWholesaleAllFilesQuery = () => useQuery({
    queryKey: ['wholesale-all-files'],
    queryFn: () => getWholesaleAllFilesAPI(),
    staleTime: 60000,
})

export let useWholesaleFileUploadMutation = () => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ file, data }) => {
            let formData = new FormData()
            formData.append('file', file)
            Object.entries(data).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    formData.append(key, value);
                }
            })
            try {
                let result = await wholesaleUploadAPI(formData)
                queryClient.invalidateQueries({ queryKey: ['wholesale-all-files'] })
                return result.data
            } catch (error) {
                return { success: false, error }
            }
        },
    })
}

export let useWholesaleAllFavoriteQuery = () => useQuery({
    queryKey: ['wholesale-all-favorite'],
    queryFn: () => getWholesaleAllFavoriteAPI(),
    staleTime: 60000,
})

export let useWholesaleCreateFavoriteMutation = () => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => createWholesaleFavoriteAPI(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wholesale-all-favorite'] })
        }
    })
}

export let useCheckFavoriteDealQuery = (id) => useQuery({
    queryKey: ['wholesale-check-favorite-deal', id],
    queryFn: () => checkFavoriteDealAPI(id),
    staleTime: Infinity,
})

export let useAddFavoriteDealMutation = (dealId) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: (favoriteId) => addFavoriteDealAPI({ deal_id: dealId, favorite_id: favoriteId }),
        onSuccess: () => {
            queryClient.setQueriesData({ queryKey: ['data', 'wholesale_wizard'] }, (oldData) => ({
                ...oldData,
                products: oldData.products.map(product => ({
                    ...product,
                    favorite: product.asin === dealId ? true : product.favorite
                }))
            }))
            queryClient.invalidateQueries({ queryKey: ['wholesale-check-favorite-deal', dealId] })
        }
    })
}

export let useRemoveFavoriteDealMutation = (dealId) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: (favoriteId) => removeFavoriteDealAPI({ deal_id: dealId, favorite_id: favoriteId }),
        onSuccess: (data, favoriteId) => {
            let dealFavorites = queryClient.getQueryData( ['wholesale-check-favorite-deal', dealId])
            let newDealFavorites = (dealFavorites?.all_favorite ?? []).filter(item => item.favorite_id !== favoriteId)
            queryClient.setQueriesData({ queryKey: ['data', 'wholesale_wizard'] }, (oldData) => {
                return {
                ...oldData,
                products: oldData.products.map(product => ({
                    ...product,
                           favorite: product.asin === dealId && !newDealFavorites.length ? false : product.favorite
                    }))
                }
            })
            queryClient.invalidateQueries({ queryKey: ['wholesale-check-favorite-deal', dealId] })
        }
    })
}

export let useDeleteFavoriteMutation = (favoriteId) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: (favoriteId) => deleteFavoriteAPI(favoriteId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wholesale-all-favorite'] })
        }
    })
}

export let useDeleteWholesaleListMutation = (listId) => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: (listId) => deleteWholesaleListAPI(listId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wholesale-all-files'] })
        }
    })
}

export let useWholesaleAIMutation = () => useMutation({
    mutationFn: ({ asin, region, cost }) => postWholesaleAIResponseAPI({ asin, region, cost }),
})

export let useWholesaleExportListMutation = () => useMutation({
    mutationFn: ({ listId, favoriteId, regions }) => exportWholesaleListAPI({ listId, favoriteId, regions }),
})

export let useWholesaleSettingsQuery = () => useQuery({
    queryKey: ['wholesale-settings'],
    queryFn: () => getWholesaleSettingsAPI(),
    staleTime: 60000,
})

export let useUpdateWholesaleSettingsMutation = () => {
    let queryClient = useQueryClient()
    return useMutation({
        mutationFn: (payload) => updateWholesaleSettingsAPI(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wholesale-settings'] })
        }
    })
}
